import { manufactureConstants } from "../../constants";

const defaultState = {
  items: [],
  item: {},
  loadingItems: false,
  loadingItem: false,
  fetchedItems: false,
  fetchedItem: false,
  error: null
};
const formatUserName = (user) => {
  if (!user) return null;
  if (typeof user === 'object') {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  return user;
};

function normalData(arr) {
  if (!Array.isArray(arr)) return [];

  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    // Если строка с временем (ISO или похожий формат) — берём только дату
    if (typeof dateValue === 'string') {
      return dateValue.split('T')[0]; // "2026-05-31T00:00:00" = "2026-05-31"
    }
    // Если Date объект — форматируем вручную, чтобы избежать проблем с часовым поясом
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return arr.map((elem, index) => ({
    ...elem,
    index: index + 1,
    dateStart: formatDate(elem.dateStart),
    dateFinish: formatDate(elem.dateFinish),
    specificationName: elem.specification?.name || elem.specificationName,
    specificationId: elem.specification?.id || elem.specificationId,
    nomenclatureName: elem.product?.name || elem.product?.nomenclature?.nameNomenclature,
    nomenclatureId: elem.product?.nomenclature?.id,
    status: elem.status || elem.productStatus,
    storageName: elem.storage?.nameStorage || elem.storage?.name,
    storageId: elem.storage?.id ?? null,
    orderNumber: elem.order?.number ?? "",
    orderLabel: elem.order?.id ?? "",
    orderId: elem.order?.id ?? null,
    contractorName: elem.contractor?.name || elem.contractor?.inn
      ? `${elem.contractor?.name}${elem.contractor?.inn ? ` (${elem.contractor.inn})` : ''}`
      : elem.contractorName,
    contractorId: (elem.contractor?.id || elem.contractorId) ?? null,
    contractorLabel: elem.contractor?.id ?? "",
    whoCreated: formatUserName(elem.whoCreated),
    whoUpdated: formatUserName(elem.whoUpdated),
  }));
}

function normalItem(elem) {
  if (!elem) return {};

  return {
    ...elem,
    specificationId: elem.specification?.id || elem.specificationId,
    nomenclatureId: elem.product?.nomenclature?.id,
    status: elem.status || elem.productStatus,
    storageId: elem.storage?.id,
    orderId: elem.order?.id,
    contractorId: elem.contractor?.id || elem.contractorId,
    whoCreated: formatUserName(elem.whoCreated),
    whoUpdated: formatUserName(elem.whoUpdated),
  };
}

export const manufactureReducer = function (state = defaultState, action) {
  switch (action.type) {

    case manufactureConstants.GETALL_REQUEST_MANUFACTURE:
      return {
        ...state,
        item: {},
        loadingItems: true,
        loadingItem: false,
        fetchedItems: false,
        fetchedItem: false,
        error: null
      };

    case manufactureConstants.GETALL_SUCCESS_MANUFACTURE:
      return {
        ...state,
        items: normalData(action.response),
        loadingItems: false,
        fetchedItems: true,
      };

    case manufactureConstants.GETALL_FAILURE_MANUFACTURE:
      return {
        ...state,
        loadingItems: false,
        fetchedItems: false,
        error: action.error,
      };

    case manufactureConstants.GETBYID_REQUEST_MANUFACTURE_ITEM:
      return {
        ...state,
        item: {},
        loadingItem: true,
        fetchedItem: false,
        error: null
      };

    case manufactureConstants.GETBYID_SUCCESS_MANUFACTURE_ITEM:
      const existingItem = state.items.find(item => item.id === action.response.id && item.viewStatus?.status !== action.response?.viewStatus?.status);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item => {
            if (item.id !== action.response.id) {
              return item;
            }
            item.whoCreated = action.response?.whoCreated?.firstName + " " + action.response?.whoCreated?.lastName;
            item.viewStatus = action.response?.viewStatus;
            return item;
          }),
          item: normalItem(action.response),
          loadingItem: false,
          fetchedItem: true,
        };
      }
      else {
        return {
          ...state,
          item: action.response,
          loadingItem: false,
          fetchedItem: true,
        };
      }

    case manufactureConstants.GETBYID_FAILURE_MANUFACTURE_ITEM:
      return {
        ...state,
        loadingItem: false,
        fetchedItem: false,
        error: action.error,
      };

    case manufactureConstants.CREATE_REQUEST_MANUFACTURE:
      return {
        ...state,
        item: {},
        loadingItem: true,
        fetchedItem: false,
        error: null
      };

    case manufactureConstants.CREATE_SUCCESS_MANUFACTURE:
      const newItems = Array.isArray(action.response)
        ? action.response.map(item => normalItem(item))
        : [normalItem(action.response)];

      return {
        ...state,
        loadingItem: false,
        fetchedItem: true,
        item: newItems[0] || {},
        items: normalData([...newItems, ...state.items]),
      };

    case manufactureConstants.CREATE_FAILURE_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        fetchedItem: false,
        error: action.error,
      };

    case manufactureConstants.UPDATE_REQUEST_MANUFACTURE_ITEM:
      return {
        ...state,
        loadingItem: true,
        fetchedItem: false,
        error: null
      };

    case manufactureConstants.UPDATE_SUCCESS_MANUFACTURE_ITEM:
      const updatedItem = normalItem(action.response);
      return {
        ...state,
        loadingItem: false,
        fetchedItem: true,
        item: updatedItem,
        items: normalData(state.items.map((it) =>
          it.id === updatedItem.id ? updatedItem : it
        )),
      };

    case manufactureConstants.UPDATE_FAILURE_MANUFACTURE_ITEM:
      return {
        ...state,
        loadingItem: false,
        fetchedItem: false,
        error: action.error,
      };

    case manufactureConstants.ARCHIVE_REQUEST_MANUFACTURE:
      return {
        ...state,
        loadingItem: true,
        fetchedItem: false,
        error: null,
      };

    case manufactureConstants.ARCHIVE_SUCCESS_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        fetchedItem: false,
        items: normalData(state.items.map((it) =>
          it.id === action.id ? { ...it, isArchive: !it.isArchive } : it
        )),
      };

    case manufactureConstants.ARCHIVE_FAILURE_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        error: action.error,
      };

    case manufactureConstants.CHANGE_STATUS_REQUEST_MANUFACTURE:
      return {
        ...state,
        loadingItem: true,
        fetchedItem: false,
        error: null,
      };

    case manufactureConstants.CHANGE_STATUS_SUCCESS_MANUFACTURE:
      const statusItem = normalItem(action.response);
      return {
        ...state,
        loadingItem: false,
        fetchedItem: true,
        item: statusItem,
        items: normalData(state.items.map((it) =>
          it.id === statusItem.id ? statusItem : it
        )),
      };

    case manufactureConstants.CHANGE_STATUS_FAILURE_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        error: action.error,
      };

    case manufactureConstants.VIEW_REQUEST_MANUFACTURE:
      return {
        ...state,
        loadingItem: true,
        fetchedItem: false,
        error: null,
      };
    case manufactureConstants.VIEW_SUCCESS_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        fetchedItem: true,
        item: {
          ...state.item,
          viewStatus: action.response?.viewStatus
        },
        items: normalData(
          state.items.map((elem) =>
            elem.id === action.response?.documentId
              ? { ...elem, viewStatus: action.response?.viewStatus }
              : elem
          )
        ),
      };
    case manufactureConstants.VIEW_FAILURE_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        error: action.error,
      };

    case manufactureConstants.DELETE_REQUEST_MANUFACTURE:
      return {
        ...state,
        loadingItem: true,
        error: null,
      };

    case manufactureConstants.DELETE_SUCCESS_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        items: normalData(state.items.filter(it => it.id !== action.id)),
      };

    case manufactureConstants.DELETE_FAILURE_MANUFACTURE:
      return {
        ...state,
        loadingItem: false,
        error: action.error,
      };

    default:
      return state;
  }
}